'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones de servicios auxiliares (asumiendo que los hiciste modulares)
import { fetchAllOrganizations } from '../Organizations/organization';
import { fetchAllBranches } from '../Branches/branch';
import { fetchAllEquipments, fetchAllServices } from '../Services/services';
import {
  fetchSlots,
  createSlot,
  updateSlot,
  deleteSlot,
  SlotAPI,
  SlotData,
} from './slot';

// Componentes Separados
import SlotFormModal from './components/SlotFormModal';
import SlotList from './components/SlotList';
import { getHeaderConfig } from '../Users/Users';

// --- Estado Inicial del Formulario ---
const INITIAL_FORM_DATA: SlotData = {
  organizationId: '',
  branchId: '',
  fk_equipment: '',
  fk_service: '',
  start: '', // YYYY-MM-DDTHH:mm
  end: '',   // YYYY-MM-DDTHH:mm
  urgency: false,
  is_available: true,
};

function SlotPanel({ servicesManager }) {
  const [slots, setSlots] = useState<SlotAPI[]>([]);

  // Dependencias para los selectores del formulario
  const [organizations, setOrganizations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [services, setServices] = useState([]);

  const [filterData, setFilterData] = useState({ branchId: '', equipmentId: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<SlotAPI | null>(null);
  const [formData, setFormData] = useState<SlotData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();
  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  // --- Lógica de Carga de Datos y Dependencias ---

  const loadDependencies = async () => {
    try {
      const [orgs, branchList, equipList, serviceList] = await Promise.all([
        fetchAllOrganizations(),
        fetchAllBranches(),
        fetchAllEquipments(),
        fetchAllServices(),
      ]);
      setOrganizations(orgs);
      setBranches(branchList);
      setEquipments(equipList);
      setServices(serviceList);
    } catch (error) {
      toast.error('Error al cargar dependencias (organizaciones, sucursales, etc.).');
    }
  };

  const loadSlots = useCallback(async () => {
    try {
      setIsLoading(true);
      // Usar los filtros definidos en el estado para la petición GET
      const apiSlots = await fetchSlots(filterData);
      setSlots(apiSlots);
    } catch (error) {
      toast.error('Error al obtener slots: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  }, [filterData]);

  useEffect(() => {
    loadDependencies();
  }, []);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  // --- Manejadores CRUD ---

  const handleCreateSlot = async () => {
    try {
      await createSlot(formData);
      toast.success(`Bloque de tiempo creado exitosamente.`);
      await loadSlots();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear slot: ' + (err.message || err));
    }
  };

  const handleUpdateSlot = async () => {
    if (!editingSlot) return;
    try {
      // Solo enviamos los campos que se pueden modificar fácilmente (start, end, urgency, is_available)
      const updateData = {
        start: formData.start,
        end: formData.end,
        urgency: formData.urgency,
        is_available: formData.is_available,
        // No permitimos cambiar las referencias (org, branch, equip, service) en el PATCH simple
      };

      await updateSlot(editingSlot._id, updateData);
      toast.success(`Slot actualizado.`);
      await loadSlots();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar slot: ' + (err.message || err));
    }
  };

  const handleDeleteSlot = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este bloque de tiempo?')) return;
    try {
      await deleteSlot(id);
      toast.success('Slot eliminado correctamente.');
      await loadSlots(); // Refrescar la lista
    } catch (err: any) {
      toast.error('Error al eliminar el slot: ' + (err.message || err));
    }
  };

  const handleEditSlot = (slot: SlotAPI) => {
    setEditingSlot(slot);
    // Mapear SlotAPI a SlotData. Usamos slice(0, 16) para formato datetime-local
    setFormData({
      organizationId: slot.domain.organization._id,
      branchId: slot.domain.branch._id,
      fk_equipment: slot.fk_equipment._id,
      fk_service: slot.fk_service._id,
      start: slot.start.slice(0, 16),
      end: slot.end.slice(0, 16),
      urgency: slot.urgency,
      is_available: slot.is_available,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingSlot(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingSlot ? handleUpdateSlot() : handleCreateSlot();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Slots de Tiempo</h1>
            <p className="mt-1">Define la disponibilidad de equipos y servicios.</p>
          </div>
          <button
            onClick={() => {
              setEditingSlot(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Crear Nuevo Slot
          </button>
        </div>

        {/* Lista de Slots */}
        <SlotList
          slots={slots}
          filterData={filterData}
          setFilterData={setFilterData}
          isLoading={isLoading}
          loadSlots={loadSlots}
          branches={branches}
          equipments={equipments}
          handleEditSlot={handleEditSlot}
          handleDeleteSlot={handleDeleteSlot}
        />
      </div>

      {/* Modal de Formulario */}
      <SlotFormModal
        isDialogOpen={isDialogOpen}
        editingSlot={editingSlot}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
        organizations={organizations}
        branches={branches}
        equipments={equipments}
        services={services}
      />
    </div>
  );
}

SlotPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default SlotPanel;
